/**
 * The pinned entries as pure rules (1.I-1 deliverable 22): rows from `sidebar.list` items and
 * `docs.titles` answers in every title state, and where a drop sends `sidebar.update`.
 */
import type { DocTitleV1, SidebarItemV1 } from '@codex/contracts';

export const IN_TRASH = 'In Trash';
export const MISSING_PAGE = 'Missing page';
export const PINS_EMPTY = 'Pin pages to keep them here.';
export const MAX_TITLE_IDS = 500;

/** What the pins know of a saved view (2.K): `null` for the whole map when views are off. */
export type ViewPinInfo = ReadonlyMap<string, { readonly name: string; readonly layout: string }>;

export const MISSING_VIEW = 'Missing view';
export const SAVED_VIEW = 'Saved view';

export type PinStatus = 'live' | 'trashed' | 'merged' | 'missing' | 'pending' | 'group' | 'view';

export interface PinRow {
  readonly id: string;
  readonly kind: SidebarItemV1['kind'];
  readonly status: PinStatus;
  readonly text: string;
  /** The document the row opens, or null when it is not a link. */
  readonly openId: string | null;
  /** The saved view the row opens (2.K), or null when it is not a live view pin. */
  readonly viewId?: string | null;
  /** A live view pin's layout (2.K), for its glyph. */
  readonly layout?: string | null;
  readonly children: readonly PinRow[];
}

function describe(
  item: SidebarItemV1,
  titles: ReadonlyMap<string, DocTitleV1>,
  views: ViewPinInfo | null | undefined,
): Pick<PinRow, 'status' | 'text' | 'openId' | 'viewId' | 'layout'> {
  if (item.kind === 'group') {
    return { status: 'group', text: item.label ?? 'Group', openId: null };
  }
  if (item.kind === 'view' || item.refId === null) {
    if (views === undefined) return { status: 'view', text: item.label ?? 'View', openId: null };
    // Views off: the label or a plain name, no link.
    if (views === null || item.refId === null) {
      return { status: 'view', text: item.label ?? SAVED_VIEW, openId: null };
    }
    const found = views.get(item.refId);
    if (!found) return { status: 'missing', text: MISSING_VIEW, openId: null };
    return {
      status: 'view',
      text: item.label ?? found.name,
      openId: null,
      viewId: item.refId,
      layout: found.layout,
    };
  }
  const found = titles.get(item.refId);
  if (!found) return { status: 'pending', text: item.label ?? 'Loading…', openId: null };
  switch (found.state) {
    case 'live':
      return { status: 'live', text: item.label ?? found.title, openId: found.id };
    case 'trashed':
      return { status: 'trashed', text: IN_TRASH, openId: null };
    case 'merged':
      return { status: 'merged', text: item.label ?? found.title, openId: found.mergedIntoId };
    case 'missing':
      return { status: 'missing', text: MISSING_PAGE, openId: null };
  }
}

/** Top-level entries and, under each group, its children, all in the order the list answered. */
export function buildPinRows(
  items: readonly SidebarItemV1[],
  titles: ReadonlyMap<string, DocTitleV1>,
  views?: ViewPinInfo | null,
): PinRow[] {
  const ids = new Set(items.map((item) => item.id));
  const byParent = new Map<string | null, SidebarItemV1[]>();
  for (const item of items) {
    // A child whose group is gone is shown at the top rather than lost.
    const parent = item.parentId !== null && ids.has(item.parentId) ? item.parentId : null;
    const list = byParent.get(parent) ?? [];
    list.push(item);
    byParent.set(parent, list);
  }
  const build = (parent: string | null): PinRow[] =>
    (byParent.get(parent) ?? []).map((item) => ({
      id: item.id,
      kind: item.kind,
      ...describe(item, titles, views),
      children: item.kind === 'group' ? build(item.id) : [],
    }));
  return build(null);
}

/** Groups are not removable from the sidebar: removing one would drop its children to the top level. */
export function canUnpin(row: { kind: string }): boolean {
  return row.kind !== 'group';
}

/** The distinct documents pinned, for one `docs.titles` request. */
export function pinTitleIds(items: readonly SidebarItemV1[]): string[] {
  const ids: string[] = [];
  for (const item of items) {
    if (item.kind === 'document' && item.refId !== null && !ids.includes(item.refId)) {
      ids.push(item.refId);
    }
  }
  return ids.slice(0, MAX_TITLE_IDS);
}

/** Document id to the pin that holds it (the first, when a document is pinned twice). */
export function pinsByDocument(items: readonly SidebarItemV1[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const item of items) {
    if (item.kind === 'document' && item.refId !== null && !map.has(item.refId)) {
      map.set(item.refId, item.id);
    }
  }
  return map;
}

/**
 * Where `sidebar.update` puts a dragged entry: after the entry now before it among its
 * siblings (`order`, the ids of one level), null for first, undefined when nothing moves.
 */
export function afterIdForDrop(
  order: readonly string[],
  activeId: string,
  overId: string,
): string | null | undefined {
  const from = order.indexOf(activeId);
  const to = order.indexOf(overId);
  if (from < 0 || to < 0 || from === to) return undefined;
  const next = order.filter((id) => id !== activeId);
  next.splice(to, 0, activeId);
  const at = next.indexOf(activeId);
  return at === 0 ? null : (next[at - 1] as string);
}

/** The list as it reads once `activeId` follows `afterId` among its own level (optimistic). */
export function reorderItems(
  items: readonly SidebarItemV1[],
  activeId: string,
  afterId: string | null,
): SidebarItemV1[] {
  const active = items.find((item) => item.id === activeId);
  if (!active) return [...items];
  const rest = items.filter((item) => item.id !== activeId);
  if (afterId === null) {
    const first = rest.findIndex((item) => item.parentId === active.parentId);
    rest.splice(first < 0 ? rest.length : first, 0, active);
    return rest;
  }
  const at = rest.findIndex((item) => item.id === afterId);
  rest.splice(at < 0 ? rest.length : at + 1, 0, active);
  return rest;
}

/** True when the pins hold a live saved-view item (the views list is read only then). */
export function hasViewPin(items: readonly SidebarItemV1[]): boolean {
  return items.some((item) => item.kind === 'view' && item.refId !== null);
}
